import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { createHash } from 'crypto-pro';
import { Wallet } from 'ethers';

import CreditsApi from '../../services/api/CreditsApi';
import ExchangeApi from '../../services/api/ExchangeApi';
import IssueApi from '../../services/api/IssueApi';
import PurchasesApi from '../../services/api/PurchasesApi';
import SaleApi from '../../services/api/SaleApi';
import {
  CREDIT_AGREEMENT_TYPE,
  CREDIT_DETAILS,
  SIGN_DOC_TYPE,
} from '../../types';
import { RootState } from '../../types';
import { showError } from '../../utils/utils';
import { arrayBufferToBase64 } from '../../utils/files/downloadFile';

import { setPush } from './app';

const setSignResultDoc = createAction<{
  name: 'releaseCfa' | 'orderUcp' | 'buyingCfa' | 'saleCfa' | 'credit';
  value: string;
}>('operations-sign/setSignResultDoc');
const setOperationsSignLoading = createAction<{
  value: boolean;
  name:
  | 'initUcp'
  | 'signUcp'
  | 'signUcpDoc'
  | 'releaseCfa'
  | 'releaseCfaDoc'
  | 'releaseCfaSign'
  | 'buyingCfa'
  | 'buyingCfaDoc'
  | 'creditConstraints'
  | 'buyingCfaSign'
  | 'credit'
  | 'creditDoc'
  | 'creditSign'
  | 'saleCfa'
  | 'saleCfaDoc'
  | 'saleCfaSign'
  | 'lastPurchase'
  | 'lastSale'
  | 'purchaseOperator'
  | 'purchaseOperatorCompany'
  | 'rejectPurchase'
  | 'getPurchase'
  | 'getSale'
  | 'getIssue'
  | 'exchange';
}>('operations-sign/setLoading');
const setSigningInfo = createAction<{
  thumbprint: string;
  date: string;
  name: string;
}>('operations-sign/setSigningInfo');
const setSignedDoc = createAction<{ name: 'releaseCfa'; value: string }>(
  'operations-sign/setSignedDoc'
);
const setPaymentIds = createAction<number[]>('operations-sign/setPaymentIds');

const initUcp = createAsyncThunk(
  'operations-sign/initUcp',
  async (
    payload: {
      id: number;
      initialTokenName?: string;
      paymentCount?: number;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ name: 'initUcp', value: true })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const { order } = store.orderDetailed.data;
      if (order && order.initiatorId) {
        const response = await IssueApi.initIssue(token, {
          accountId,
          paymentCount: payload?.paymentCount,
          issues: store.realizationPositions.data.signingIds.map((item) => ({
            tokenType: 'HYBRID',
            financialClaim:
              order.items.find((oItem) => oItem.id === item)?.priceWithVat ?? 0,
            initialTokenNames: [payload.initialTokenName || 'TMX_SIMPLE'],
            initialPurchasers: [order.recipientId],
            orderItemId: item,
          })),
        });
        if (response.status === 200) {
          return response.data;
        }
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ name: 'initUcp', value: false })
      );
    }

    return undefined;
  }
);

const postRedeem = createAsyncThunk(
  'operations-sign/postRedeem',
  async (
    payload: { id: string; signature: string; initiatorId: number },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'releaseCfaDoc' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await IssueApi.postRedeem(token, payload);
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'releaseCfaDoc' })
      );
    }
    return undefined;
  }
);
const getUcpDoc = createAsyncThunk(
  'operations-sign/ucp-doc',
  async (
    payload: {
      id: number;
      type?: SIGN_DOC_TYPE;
      contentType?: string;
      preventDocConvertion?: boolean;
      withHash?: boolean;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ name: 'signUcpDoc', value: true })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await IssueApi.getIssueDoc(
        token,
        payload.id,
        payload.type,
        payload.contentType
      );
      let bufferHash = '';
      if (payload.withHash) {
        try {
          bufferHash = await createHash(response.data);
        } catch (e) {
          console.log('Hash creation failed', e);
        }
      }
      console.log('Actual version');
      return {
        // doc: btoa(
        //   new TextDecoder('latin1').decode(new Uint8Array(response.data))
        // ),
        doc: payload.preventDocConvertion
          ? ''
          : arrayBufferToBase64(response.data),
        hash: bufferHash,
      };
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ name: 'signUcpDoc', value: false })
      );
    }
    return undefined;
  }
);

const signUcp = createAsyncThunk(
  'operations-sign/signUcp',
  async (
    payload: {
      hash: string;
      docType: SIGN_DOC_TYPE;
      issuingDecisionId: number;
      signature: string;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'signUcp' })
      );
      const store = thunkAPI.getState() as RootState;
      const { hash, docType, signature, issuingDecisionId } = payload;
      const requestDto = {
        hash,
        documentType: docType,
        signature,
        accountId: store.auth.account.id,
      };
      const checksum = await createHash(JSON.stringify(requestDto));
      const response = await IssueApi.signDecision(store.auth.account.token, {
        issuingDecisionId,
        hash,
        documentType: docType,
        accountId: store.auth.account.id,
        signature: signature.replace(/\r?\n|\r/g, ''),
        checksum,
      });
      if (response.status === 200) {
        thunkAPI.dispatch(
          setSignResultDoc({ name: 'orderUcp', value: signature })
        );
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'signUcp' })
      );
    }
    return undefined;
  }
);

const releaseCfa = createAsyncThunk(
  'operations-sign/releaseCfa',
  async (
    payload: {
      claim: number;
      companyId: number;
      paymentType: string;
      tokenCount: number;
      tokenPrice: number;
      paymentCount?: number;
      minDate?: Date;
      expiryDate?: Date;
      provisionText?: string;
      commentText?: string;
      liabilityText?: string;
      publicRelease?: boolean;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'releaseCfa' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const issueResponse = await IssueApi.initFinanceIssue(token, {
        accountId,
        purchaserId: payload.companyId,
        minUsageDate: payload.minDate,
        expiryDate: payload.expiryDate,
        count: payload.tokenCount,
        price: payload.tokenPrice,
        comment: payload.commentText,
        provisionText: payload.provisionText,
        financialClaim: payload.claim,
        tokenType: 'SIMPLE',
        paymentCode: payload.paymentType,
        paymentCount: payload.paymentCount,
        publicRelease: payload.publicRelease,
        // liabilityText: payload.liabilityText
      });
      if (issueResponse.status === 200) {
        return { ...issueResponse.data, claim: payload.claim, data: payload };
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'releaseCfa' })
      );
    }

    return undefined;
  }
);

const getDecisionDoc = createAsyncThunk(
  'operations-sign/getDecisionDoc',
  async (payload: { id: number; withHash?: boolean }, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'releaseCfaDoc' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const docResponse = await IssueApi.getIssueDoc(token, payload.id);
      if (docResponse.status === 200) {
        let bufferHash = '';
        if (payload.withHash) {
          try {
            bufferHash = await createHash(docResponse.data);
          } catch (e) {
            console.log('Hash creation failed', e);
          }
        }
        return {
          doc: arrayBufferToBase64(docResponse.data),
          hash: bufferHash,
        };
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'releaseCfaDoc' })
      );
    }
    return undefined;
  }
);

const signOperation = createAsyncThunk(
  'operations-sign/sign',
  async (
    payload: {
      hash: string;
      docType: SIGN_DOC_TYPE;
      issuingDecisionId: number;
      signature: string;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'releaseCfaSign' })
      );
      const store = thunkAPI.getState() as RootState;
      const { hash, docType, signature, issuingDecisionId } = payload;
      const requestDto = {
        hash,
        documentType: docType,
        signature,
        accountId: store.auth.account.id,
      };
      const checksum = await createHash(JSON.stringify(requestDto));
      const response = await IssueApi.signDecision(store.auth.account.token, {
        issuingDecisionId,
        hash,
        documentType: docType,
        accountId: store.auth.account.id,
        signature: signature.replace(/\r?\n|\r/g, ''),
        checksum,
      });
      if (response.status === 200) {
        thunkAPI.dispatch(
          setSignResultDoc({ name: 'releaseCfa', value: signature })
        );
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'releaseCfaSign' })
      );
    }
    return undefined;
  }
);

const initPurchase = createAsyncThunk(
  'operations-sign/initPurchase',
  async (
    payload: {
      info: {
        accountId?: number;
        amount?: number;
        companyId?: number;
        tokenCount?: number;
        tokenName?: string;
        personal?: boolean;
        expiryDate?: string;
      };
      callback?: (data: any) => void;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'buyingCfa' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await PurchasesApi.initPurchase(token, payload.info);
      if (response.status === 200) {
        payload.callback && payload.callback({ ...response.data, ...payload });
        return { ...response.data, ...payload };
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'buyingCfa' })
      );
    }
    return undefined;
  }
);

const getAgreementDoc = createAsyncThunk(
  'operations-sign/getAgreementBuyingDoc',
  async (
    payload: {
      decisionId: number;
      type: 'AGREEMENT' | 'COUNTER_APPLICATION' | 'PAYMENT_CONFIRMATION';
      signed: boolean;
      withHash?: boolean;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'buyingCfaDoc' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const response = await PurchasesApi.getPurchaseAgreement(token, {
        id: payload.decisionId,
        type: payload.type,
        signed: payload.signed,
        accountId,
      });
      console.log('file response', response);
      if (response.status === 200) {
        let bufferHash = '';
        if (payload.withHash) {
          try {
            bufferHash = await createHash(response.data);
          } catch (e) {
            console.log('Hash creation failed', e);
          }
        }
        return {
          doc: response.data,
          hash: bufferHash,
        };
      }
    } catch (err) {
      console.log('file error', err);
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'buyingCfaDoc' })
      );
    }
    return undefined;
  }
);

const getLastPurchases = createAsyncThunk(
  'operations-sign/getLastPurchases',
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'lastPurchase' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const response = await PurchasesApi.getLastPurchases(token, {
        accountId,
      });
      if (response.status === 200) {
        return response.data;
      }
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'lastPurchase' })
      );
    }
    return {};
  }
);

const getPurchase = createAsyncThunk(
  'operations-sign/getPurchase',
  async (payload: { id: number }, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'getPurchase' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const response = await PurchasesApi.getPurchase(token, {
        id: payload.id,
        accountId,
      });
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'getPurchase' })
      );
    }
    return {};
  }
);

const getLastSale = createAsyncThunk(
  'operations-sign/getLastSale',
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'lastSale' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const response = await SaleApi.getLastSale(token, { accountId });
      if (response.status === 200) {
        return response.data;
      }
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'lastSale' })
      );
    }
    return {};
  }
);

const getSale = createAsyncThunk(
  'operations-sign/getSale',
  async (payload: { id: number }, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'getSale' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const response = await SaleApi.getSale(token, {
        id: payload.id,
        accountId,
      });
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'getSale' })
      );
    }
    return {};
  }
);

const getSaleAgreementDoc = createAsyncThunk(
  'operations-sign/getSaleAgreementDoc',
  async (
    payload: {
      decisionId: number;
      type?: 'AGREEMENT' | 'COUNTER_APPLICATION' | 'PAYMENT_CONFIRMATION';
      signed: boolean;
      withHash?: boolean;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'buyingCfaDoc' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const response = await SaleApi.getAgreement(token, {
        saleId: payload.decisionId,
        type: payload.type,
        signed: payload.signed,
        accountId,
      });
      if (response.status === 200) {
        let bufferHash = '';
        if (payload.withHash) {
          try {
            bufferHash = await createHash(response.data);
          } catch (e) {
            console.log('Hash creation failed', e);
          }
        }
        return {
          doc: arrayBufferToBase64(response.data),
          hash: bufferHash,
        };
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'buyingCfaDoc' })
      );
    }
    return undefined;
  }
);

const getPurchasesOperator = createAsyncThunk(
  'operations-sign/getPurchasesOperator',
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'purchaseOperator' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await PurchasesApi.getPurchasesOperator(token);
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'purchaseOperator' })
      );
    }
    return {};
  }
);

const getPurchasesOperatorCompany = createAsyncThunk(
  'operations-sign/getPurchasesOperatorCompany',
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({
          value: true,
          name: 'purchaseOperatorCompany',
        })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await PurchasesApi.getPurchasesOperatorCompany(token);
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({
          value: false,
          name: 'purchaseOperatorCompany',
        })
      );
    }
    return {};
  }
);

const signPurchase = createAsyncThunk(
  'operations-sign/signPurchase',
  async (
    payload: {
      info: {
        hash: string;
        operationId: number;
        signature: string;
      };
      callback?: () => void;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'buyingCfaSign' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const { hash, signature, operationId } = payload.info;
      const requestDto = {
        hash,
        signature,
        operationId,
        accountId,
      };
      const checksum = await createHash(JSON.stringify(requestDto));
      const response = await PurchasesApi.signPurchase(token, {
        ...requestDto,
        checksum,
      });
      if (response.status === 200) {
        thunkAPI.dispatch(
          setPush({ message: 'Заявка успешно оформлена', type: 'success' })
        );
        thunkAPI.dispatch(
          setSignResultDoc({ name: 'buyingCfa', value: signature })
        );
        payload.callback && payload.callback();
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'buyingCfaSign' })
      );
    }
    return undefined;
  }
);

const paymentPurchaseUpload = createAsyncThunk(
  'operations-sign/paymentPurchaseUpload',
  async (
    payload: {
      info: {
        id: number;
        file: Blob;
      };
      callback?: () => void;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'buyingCfaSign' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await PurchasesApi.paymentUpload(token, payload.info);
      if (response.status === 200) {
        payload.callback && payload.callback();
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'buyingCfaSign' })
      );
    }
    return {};
  }
);

const paymentPurchaseConfirm = createAsyncThunk(
  'operations-sign/paymentPurchaseConfirm',
  async (
    payload: {
      info: {
        id: number;
        signature: string;
        payload: { from: number; to: number; value: number; key: string };
      };
      callback?: () => void;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'getIssue' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await PurchasesApi.paymentConfirm(token, payload.info);
      if (response.status === 200) {
        const rawTransaction = response.data;
        if (!rawTransaction.data.startsWith('0x')) {
          rawTransaction.data = '0x' + rawTransaction.data;
        }
        const wallet = new Wallet(payload.info.payload.key);
        const signedTransaction = await wallet.signTransaction({
          nonce: rawTransaction.nonce,
          gasPrice: rawTransaction.gasPrice,
          gasLimit: rawTransaction.gasLimit,
          to: rawTransaction.to,
          value: rawTransaction.value,
          data: rawTransaction.data,
          chainId: 700,
        });
        const signaturePayload = {
          operationId: payload.info.id,
          data: signedTransaction,
          publicKey: wallet.publicKey,
        };
        const result = await PurchasesApi.paymentConfirmPublish(
          token,
          signaturePayload
        );
        if (result.status === 200) {
          thunkAPI.dispatch(
            setPush({
              message: 'Транзакция успешно подписана',
              type: 'success',
            })
          );
          payload.callback && payload.callback();
          return response.data;
        } else {
          thunkAPI.dispatch(
            setPush({ message: 'Ошибка валидации Ключа', type: 'error' })
          );
        }
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'getIssue' })
      );
    }
    return {};
  }
);

const paymentSaleConfirm = createAsyncThunk(
  'operations-sign/paymentSaleConfirm',
  async (
    payload: {
      info: {
        id: number;
        signature: string;
        payload: { from: number; to: number; value: number; key: string };
      };
      callback?: () => void;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'getIssue' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await SaleApi.paymentConfirm(token, payload.info);
      if (response.status === 200) {
        const rawTransaction = response.data;
        if (!rawTransaction.data.startsWith('0x')) {
          rawTransaction.data = '0x' + rawTransaction.data;
        }
        const wallet = new Wallet(payload.info.payload.key);
        const signedTransaction = await wallet.signTransaction({
          nonce: rawTransaction.nonce,
          gasPrice: rawTransaction.gasPrice,
          gasLimit: rawTransaction.gasLimit,
          to: rawTransaction.to,
          value: rawTransaction.value,
          data: rawTransaction.data,
          chainId: 700,
        });
        const signaturePayload = {
          operationId: payload.info.id,
          data: signedTransaction,
          publicKey: wallet.publicKey,
        };
        const result = await SaleApi.paymentConfirmPublish(
          token,
          signaturePayload
        );
        if (result.status === 200) {
          thunkAPI.dispatch(
            setPush({
              message: 'Транзакция успешно подписана',
              type: 'success',
            })
          );
          payload.callback && payload.callback();
          return response.data;
        } else {
          thunkAPI.dispatch(
            setPush({ message: 'Ошибка валидации Ключа', type: 'error' })
          );
        }
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'getIssue' })
      );
    }
    return {};
  }
);

const holdSale = createAsyncThunk(
  'operations-sign/holdSale',
  async (
    payload: {
      info: {
        operationId: number;
        privateKey: string;
      };
      callback?: () => void;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'getIssue' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await SaleApi.signHold(token, payload.info);

      if (response.status === 200) {
        const rawTransaction = response.data;

        if (!rawTransaction.data.startsWith('0x')) {
          rawTransaction.data = '0x' + rawTransaction.data;
        }
        const wallet = new Wallet(payload.info.privateKey);
        const signedTransaction = await wallet.signTransaction({
          nonce: rawTransaction.nonce,
          gasPrice: rawTransaction.gasPrice,
          gasLimit: rawTransaction.gasLimit,
          to: rawTransaction.to,
          value: rawTransaction.value,
          data: rawTransaction.data,
          chainId: 700,
        });
        const signaturePayload = {
          operationId: payload.info.operationId,
          data: signedTransaction,
          publicKey: wallet.publicKey,
        };
        const result = await SaleApi.signHoldPublish(token, signaturePayload);
        if (result.status === 200) {
          thunkAPI.dispatch(
            setPush({
              message: 'Транзакция успешно подписана',
              type: 'success',
            })
          );
          payload.callback && payload.callback();
          return response.data;
        } else {
          thunkAPI.dispatch(
            setPush({ message: 'Ошибка валидации Ключа', type: 'error' })
          );
        }
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'getIssue' })
      );
    }
    return {};
  }
);

const cryptoSignExchange = createAsyncThunk(
  'operations-sign/cryptoSignExchange',
  async (
    payload: {
      info: {
        operationId: number;
        privateKey: string;
      };
      callback: () => void;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'getIssue' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await ExchangeApi.cryptoSign(token, payload.info);

      if (response.status === 200) {
        thunkAPI.dispatch(
          setPush({ message: 'Транзакция успешно подписана', type: 'success' })
        );
        payload.callback && payload.callback();
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'getIssue' })
      );
    }
    return {};
  }
);

const holdPurchase = createAsyncThunk(
  'operations-sign/holdPurchase',
  async (
    payload: {
      info: {
        operationId: number;
        privateKey: string;
      };
      callback?: () => void;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'getIssue' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await PurchasesApi.signHold(token, payload.info);
      if (response.status === 200) {
        const rawTransaction = response.data;

        if (!rawTransaction.data.startsWith('0x')) {
          rawTransaction.data = '0x' + rawTransaction.data;
        }
        const wallet = new Wallet(payload.info.privateKey);
        const signedTransaction = await wallet.signTransaction({
          nonce: rawTransaction.nonce,
          gasPrice: rawTransaction.gasPrice,
          gasLimit: rawTransaction.gasLimit,
          to: rawTransaction.to,
          value: rawTransaction.value,
          data: rawTransaction.data,
          chainId: 700,
        });
        const signaturePayload = {
          operationId: payload.info.operationId,
          data: signedTransaction,
          publicKey: wallet.publicKey,
        };

        const result = await PurchasesApi.signHoldPublish(
          token,
          signaturePayload
        );

        if (result.status === 200) {
          thunkAPI.dispatch(
            setPush({
              message: 'Транзакция успешно подписана',
              type: 'success',
            })
          );
          payload.callback && payload.callback();
          return result.data;
        } else {
          thunkAPI.dispatch(
            setPush({ message: 'Ошибка валидации Ключа', type: 'error' })
          );
        }
      }
    } catch (error) {
      showError(error, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'getIssue' })
      );
    }
    return undefined;
  }
);

const paymentSaleUpload = createAsyncThunk(
  'operations-sign/paymentSaleUpload',
  async (
    payload: {
      info: {
        id: number;
        file: Blob;
      };
      callback?: () => void;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'buyingCfaSign' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await SaleApi.paymentUpload(token, payload.info);
      if (response.status === 200) {
        payload.callback && payload.callback();
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'buyingCfaSign' })
      );
    }
    return {};
  }
);

const getCreditAgreement = createAsyncThunk(
  'operations/getCreditAgreement',
  async (
    payload: {
      creditId: number;
      type: CREDIT_AGREEMENT_TYPE;
      withHash?: boolean;
    },
    thunkApi
  ) => {
    try {
      thunkApi.dispatch(
        setOperationsSignLoading({ value: true, name: 'creditDoc' })
      );
      const store = thunkApi.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const response = await CreditsApi.getCreditAgreement(token, {
        accountId,
        creditId: payload.creditId,
        type: payload.type,
      });
      if (response.status === 200) {
        let bufferHash = '';
        if (payload.withHash) {
          try {
            bufferHash = await createHash(response.data);
          } catch (e) {
            console.log('Hash creation failed', e);
          }
        }
        return {
          doc: arrayBufferToBase64(response.data),
          hash: bufferHash,
        };
      }
    } catch (err) {
      showError(err, thunkApi);
    } finally {
      thunkApi.dispatch(
        setOperationsSignLoading({ value: false, name: 'creditDoc' })
      );
    }
    return undefined;
  }
);

const initCredit = createAsyncThunk(
  'operations-sign/initCredit',
  async (
    payload: {
      amount: number;
      term: number;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'credit' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const response = await CreditsApi.initCredit(token, {
        amount: payload.amount,
        accountId,
        duration: payload.term,
      });

      if (response.status === 200) {
        thunkAPI.dispatch(
          getCreditAgreement({
            creditId: response.data.id,
            type: 'RECEIVING',
            withHash: true,
          })
        );
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'credit' })
      );
    }

    return undefined;
  }
);

const setCreditDetails = createAction<CREDIT_DETAILS>(
  'operations-sign/setCreditDetails'
);

const signCredit = createAsyncThunk(
  'operations-sign/signCredit',
  async (
    payload: {
      operationId: number;
      type: CREDIT_AGREEMENT_TYPE;
      signature: string;
      hash: string;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'creditSign' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const checksum = await createHash(
        JSON.stringify({ ...payload, accountId })
      );
      const response = await CreditsApi.signCreditAgreement(token, {
        ...payload,
        checksum,
        accountId,
      });
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'creditSign' })
      );
    }
    return undefined;
  }
);

const getCreditConstraints = createAsyncThunk(
  'operations-sign/getCreditConstraints',
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'creditConstraints' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await CreditsApi.getCreditConstraints(token);
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'creditConstraints' })
      );
    }
    return undefined;
  }
);

const initSale = createAsyncThunk(
  'operations-sign/initSale',
  async (
    payload: {
      accountId?: number;
      amount?: number;
      companyId?: number;
      tokenCount?: number;
      tokenName?: string;
      personal?: boolean;
      expiryDate?: string;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'saleCfa' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await SaleApi.initSale(token, payload);
      if (response.status === 200) return { ...response.data, ...payload };
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'saleCfa' })
      );
    }
    return undefined;
  }
);

const getSaleAgreement = createAsyncThunk(
  'operations/getSaleAgreement',
  async (
    payload: {
      saleId: number;
      signed: boolean;
      type?: 'AGREEMENT' | 'COUNTER_APPLICATION' | 'PAYMENT_CONFIRMATION';
      withHash?: boolean;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'saleCfaDoc' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const response = await SaleApi.getAgreement(token, {
        saleId: payload.saleId,
        signed: payload.signed,
        type: payload.type,
        accountId,
      });
      if (response.status === 200) {
        let bufferHash = '';
        if (payload.withHash) {
          try {
            bufferHash = await createHash(response.data);
          } catch (e) {
            console.log('Hash creation failed', e);
          }
        }

        return {
          doc: arrayBufferToBase64(response.data),
          hash: bufferHash,
        };
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'saleCfaDoc' })
      );
    }
    return undefined;
  }
);

const signSale = createAsyncThunk(
  'operations/signSale',
  async (
    payload: {
      hash: string;
      operationId: number;
      signature: string;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'saleCfaSign' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const { hash, signature, operationId } = payload;
      const requestDto = {
        hash,
        signature,
        operationId,
        accountId,
      };
      const checksum = await createHash(JSON.stringify(requestDto));
      const response = await SaleApi.signAgreement(token, {
        ...requestDto,
        checksum,
      });
      if (response.status === 200) {
        thunkAPI.dispatch(
          setSignResultDoc({ name: 'saleCfa', value: signature })
        );
        thunkAPI.dispatch(
          setPush({ message: 'Документ успешно подписан', type: 'success' })
        );
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'saleCfaSign' })
      );
    }
    return undefined;
  }
);

const rejectPurchase = createAsyncThunk(
  'operations/rejectPurchase',
  async (
    payload: {
      hash: string;
      operationId: number;
      signature: string;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'rejectPurchase' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const { hash, signature, operationId } = payload;
      const requestDto = {
        hash,
        signature,
        operationId,
        accountId,
      };
      const checksum = await createHash(JSON.stringify(requestDto));
      const response = await PurchasesApi.rejectPurchase(token, {
        ...requestDto,
        checksum,
      });
      if (response.status === 200) {
        thunkAPI.dispatch(
          setPush({ message: 'Заявка успешно отменена', type: 'success' })
        );
        thunkAPI.dispatch(
          setOperationsSignLoading({ value: false, name: 'rejectPurchase' })
        );
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'rejectPurchase' })
      );
    }
    return undefined;
  }
);

const getIssue = createAsyncThunk(
  'operations-sign/getIssue',
  async (payload: { id: number }, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'getIssue' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const response = await IssueApi.getIssue(token, {
        id: payload.id,
        accountId,
      });
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'getIssue' })
      );
    }
    return {};
  }
);

const paymentUpload = createAsyncThunk(
  'operations-sign/paymentUpload',
  async (payload: { id: number; file: Blob }, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'getIssue' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await IssueApi.paymentUpload(token, payload);
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'getIssue' })
      );
    }
    return {};
  }
);

const signIssue = createAsyncThunk(
  'operations-sign/signIssue',
  async (
    payload: {
      info: {
        decisionId: number;
        signedData: string;
        privateKey: string;
      };
      callback: () => void;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'getIssue' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await IssueApi.signIssue(token, payload.info);
      if (response.status === 200) {
        payload.callback && payload.callback();
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'getIssue' })
      );
    }
    return {};
  }
);

const paymentConfirm = createAsyncThunk(
  'operations-sign/paymentConfirm',
  async (
    payload: { id: number; signedData: string; privateKey: string },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'getIssue' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await IssueApi.paymentConfirm(token, payload);
      if (response.status === 200) {
        return response.data;
      }
    } catch (error: any) {
      showError(error, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'getIssue' })
      );
    }
    return {};
  }
);

const initExchange = createAsyncThunk(
  'operations-sign/initExchange',
  async (
    payload: {
      agentId?: number;
      tokenCountFrom?: number;
      tokenCountTo?: number;
      tokenNameFrom?: string;
      tokenNameTo?: string;
      isPublic?: boolean;
      expiryDate?: Date;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'exchange' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await ExchangeApi.initExchange(token, payload);
      if (response.status === 200) return { ...response.data, ...payload };
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'exchange' })
      );
    }
    return undefined;
  }
);

const signExchange = createAsyncThunk(
  'operations/signExchange',
  async (
    payload: {
      hash: string;
      id: number;
      signature: string;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: true, name: 'saleCfaSign' })
      );
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const { hash, signature, id } = payload;
      const requestDto = {
        hash,
        signature,
        id,
        accountId,
        documentType: 'DECISION',
      };
      const checksum = await createHash(JSON.stringify(requestDto));
      const response = await ExchangeApi.signExchange(token, {
        ...requestDto,
        checksum,
      });
      if (response.status === 200) {
        thunkAPI.dispatch(
          setSignResultDoc({ name: 'saleCfa', value: signature })
        );
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setOperationsSignLoading({ value: false, name: 'saleCfaSign' })
      );
    }
    return undefined;
  }
);

export {
  setOperationsSignLoading,
  releaseCfa,
  getDecisionDoc,
  setSigningInfo,
  setSignedDoc,
  signOperation,
  initPurchase,
  signPurchase,
  getAgreementDoc,
  getCreditConstraints,
  initCredit,
  getCreditAgreement,
  signCredit,
  setCreditDetails,
  initSale,
  getSaleAgreement,
  signSale,
  signUcp,
  initUcp,
  getUcpDoc,
  setSignResultDoc,
  setPaymentIds,
  getLastPurchases,
  getPurchasesOperator,
  getPurchasesOperatorCompany,
  getLastSale,
  rejectPurchase,
  getPurchase,
  getSale,
  getIssue,
  paymentUpload,
  initExchange,
  signExchange,
  cryptoSignExchange,
  paymentConfirm,
  signIssue,
  paymentPurchaseUpload,
  paymentSaleUpload,
  holdSale,
  holdPurchase,
  paymentSaleConfirm,
  getSaleAgreementDoc,
  paymentPurchaseConfirm,
  postRedeem,
};
